import React, { useEffect, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import Navbar from "./pages/Navbar/Navbar";
import MainRoutes from "./pages/Route/route";
import Footer from "./pages/Footer/Footer";
import "../src/App.css";
import { rememberPage } from "./Utils/authHelper";
import Loader from "./Components/Loader/Loader";
import CookieBanner from "./Components/CookieBanner";
import { CartProvider } from "./context/CartContext";
import { NavbarProvider, useNavbar } from "./context/NavbarContext";
import GlamToastContainer from "./Components/Toast/GlamToastContainer";
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';

const theme = createTheme({
  palette: {
    primary: {
      main: '#C8A054', // Metallic Gold
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9A7428', // Deeper Gold (Bronze)
    },
    background: {
      default: '#F6F4EF',
    }
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontWeight: 500 },
    h2: { fontWeight: 500 },
    h3: { fontWeight: 500 },
    h4: { fontWeight: 500, letterSpacing: '0.02em' },
    h5: { fontWeight: 500, letterSpacing: '0.01em' },
    h6: { fontWeight: 500 },
    subtitle1: { fontWeight: 400 },
    subtitle2: { fontWeight: 400 },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400 },
    button: { fontWeight: 500, textTransform: 'none' },
  }
});

// ScrollToTop component to reset scroll position on every navigation
const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  // remember the last shopping page so signing in can return to it
  useEffect(() => {
    rememberPage(pathname, search);
  }, [pathname, search]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' // Instant for immediate feedback during navigation
    });
  }, [pathname]);

  return null;
};

// the full-screen splash is skipped on the sign in / sign up / reset pages: people opening those want the form straight away
const SKIP_SPLASH_PATHS = ["/signin", "/signup", "/business/signin", "/business/signup"];
const skipSplash = () => SKIP_SPLASH_PATHS.includes(window.location.pathname) || window.location.pathname.startsWith("/reset-password");

function App() {
  const [loading, setLoading] = useState(() => !skipSplash());

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <Loader />;
  }

  function AppLayout() {
    const { isNavbarVisible } = useNavbar();
    const location = useLocation();
    const hideNavbarByPath = location.pathname.startsWith("/reset-password") || location.pathname === "/terms-and-conditions" || location.pathname === "/privacy-policy";
    const hideFooterByPath = ["/signin", "/signup", "/business/signin", "/business/signup"].includes(location.pathname) || location.pathname.startsWith("/reset-password");

    return (
      <>
        <ScrollToTop />
        {isNavbarVisible && !hideNavbarByPath && <Navbar />}
        <GlamToastContainer />
        <MainRoutes />
        {!hideFooterByPath && <Footer />}
        <CookieBanner />
      </>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CartProvider>
        <NavbarProvider>
          <BrowserRouter>
            <AppLayout />
          </BrowserRouter>
        </NavbarProvider>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;
